import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Download, FileText, Package, Truck, CheckCircle2, Plus, Trash2, Upload, Pen, X, AlertTriangle, Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import WinRateStrip from '@/components/WinRateStrip';

const API = process.env.REACT_APP_BACKEND_URL;

// Request config: attach ONLY the Bearer token (localStorage). We deliberately do
// NOT set withCredentials on cross-origin requests here — the Emergent ingress
// emits "Access-Control-Allow-Origin: *" and a credentialed request against a
// wildcard origin is blocked by every browser (surfaces as "Network Error" in
// axios). Bearer-token auth works universally because services/auth.py reads
// either the session_token cookie OR the Authorization header.
const getToken = () => localStorage.getItem('mosaic_session_token');
const authCfg = () => {
  const token = getToken();
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

const STATUS_META = {
  pending:   { label: 'Pending',   color: 'bg-gray-100 text-gray-700 border-gray-300' },
  invoiced:  { label: 'Invoiced',  color: 'bg-blue-50 text-blue-700 border-blue-300' },
  packed:    { label: 'Packed',    color: 'bg-amber-50 text-amber-700 border-amber-300' },
  shipped:   { label: 'Shipped',   color: 'bg-indigo-50 text-indigo-700 border-indigo-300' },
  delivered: { label: 'Delivered', color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  cancelled: { label: 'Cancelled', color: 'bg-red-50 text-red-700 border-red-300' },
};

const EMPTY_CONTACT = {
  school_name: '', school_dbn: '', school_address: '',
  contact_name: '', contact_email: '', contact_phone: '',
};

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'shipped' | 'delivered'

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/orders`, authCfg());
      setOrders(r.data.orders || []);
    } catch (e) {
      toast.error('Failed to load orders: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="orders-tab">
      <WinRateStrip />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Orders & Fulfillment
          </h2>
          <p className="text-sm text-gray-500">
            Track purchase orders received from schools. Download Invoice, Packing Slip, and Delivery/POD documents. Mark as delivered with signature capture.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="new-order-btn">
          <Plus size={16} /> New Order
        </Button>
      </div>

      {/* Clickable KPI cards */}
      {!loading && orders.length > 0 && (() => {
        const totalCount     = orders.length;
        const pendingCount   = orders.filter(o => o.status === 'pending' || o.status === 'invoiced').length;
        const shippedCount   = orders.filter(o => o.status === 'shipped').length;
        const deliveredCount = orders.filter(o => o.status === 'delivered').length;
        const totalRevenue   = orders.reduce((s, o) => s + (o.total || 0), 0);
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`bg-white border-2 p-4 text-left rounded-lg transition-all hover:shadow-md hover:border-indigo-500 ${statusFilter === 'all' ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-gray-100'}`}
                data-testid="stat-total-orders"
                title="Show all orders"
              >
                <div className="text-xs text-gray-500 font-medium">Total Orders</div>
                <div className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{totalCount}</div>
                {statusFilter === 'all' && <div className="text-[10px] text-indigo-700 font-bold mt-0.5">▸ showing all</div>}
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
                className={`bg-white border-2 p-4 text-left rounded-lg transition-all hover:shadow-md hover:border-amber-500 ${statusFilter === 'pending' ? 'border-amber-500 ring-2 ring-amber-500/20 bg-amber-50/30' : 'border-gray-100'}`}
                data-testid="stat-pending-orders"
                title="Filter to Pending / Invoiced (not yet shipped)"
              >
                <div className="text-xs text-gray-500 font-medium">Pending</div>
                <div className="text-3xl font-black mt-1 text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{pendingCount}</div>
                {statusFilter === 'pending' && <div className="text-[10px] text-amber-700 font-bold mt-0.5">▸ filter active</div>}
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'shipped' ? 'all' : 'shipped')}
                className={`bg-white border-2 p-4 text-left rounded-lg transition-all hover:shadow-md hover:border-indigo-500 ${statusFilter === 'shipped' ? 'border-indigo-500 ring-2 ring-indigo-500/20 bg-indigo-50/30' : 'border-gray-100'}`}
                data-testid="stat-shipped-orders"
                title="Filter to Shipped (in transit)"
              >
                <div className="text-xs text-gray-500 font-medium">Shipped</div>
                <div className="text-3xl font-black mt-1 text-indigo-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{shippedCount}</div>
                {statusFilter === 'shipped' && <div className="text-[10px] text-indigo-700 font-bold mt-0.5">▸ filter active</div>}
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'delivered' ? 'all' : 'delivered')}
                className={`bg-white border-2 p-4 text-left rounded-lg transition-all hover:shadow-md hover:border-emerald-500 ${statusFilter === 'delivered' ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/30' : 'border-gray-100'}`}
                data-testid="stat-delivered-orders"
                title="Filter to Delivered (POD captured)"
              >
                <div className="text-xs text-gray-500 font-medium">Delivered</div>
                <div className="text-3xl font-black mt-1 text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{deliveredCount}</div>
                {statusFilter === 'delivered' && <div className="text-[10px] text-emerald-700 font-bold mt-0.5">▸ filter active</div>}
              </button>
              <div className="bg-white border-2 border-gray-100 p-4 rounded-lg" data-testid="stat-total-revenue">
                <div className="text-xs text-gray-500 font-medium">Total Revenue</div>
                <div className="text-2xl font-black mt-1 text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            {statusFilter !== 'all' && (
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs" data-testid="orders-active-filter-bar">
                <span className="font-semibold text-slate-700">Active filter:</span>
                <span className="bg-white border border-slate-300 rounded px-2 py-0.5 font-medium text-slate-800 capitalize">
                  {statusFilter}
                </span>
                <span className="text-slate-500 ml-1">
                  · {orders.filter(o => (statusFilter === 'pending' ? (o.status === 'pending' || o.status === 'invoiced') : o.status === statusFilter)).length} of {totalCount} orders shown
                </span>
                <button
                  onClick={() => setStatusFilter('all')}
                  className="ml-auto text-indigo-700 hover:underline font-bold"
                  data-testid="orders-clear-filter-btn"
                >
                  Clear filter
                </button>
              </div>
            )}
          </>
        );
      })()}

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Order #</th>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-700">PO #</th>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-700">School</th>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Status</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-700">Items</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-700">Total</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                No orders yet. Click <b>New Order</b> to record a received PO, or convert a Quote from the Quotes tab.
              </td></tr>
            )}
            {orders
              .filter(o => {
                if (statusFilter === 'all') return true;
                if (statusFilter === 'pending') return o.status === 'pending' || o.status === 'invoiced';
                return o.status === statusFilter;
              })
              .map((o, i) => (
              <tr key={o.order_id} className={i % 2 ? 'bg-gray-50' : 'bg-white'} data-testid={`order-row-${o.order_number}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-indigo-700 font-semibold">{o.order_number}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{o.po_number}</td>
                <td className="px-4 py-2.5">{o.bill_to?.school_name || '—'}</td>
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={STATUS_META[o.status]?.color}>{STATUS_META[o.status]?.label}</Badge>
                </td>
                <td className="px-4 py-2.5 text-right">{o.items?.reduce((s, it) => s + it.quantity, 0)}</td>
                <td className="px-4 py-2.5 text-right font-semibold">${(o.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setActiveOrder(o)} data-testid={`open-order-${o.order_number}`}>Open</Button>
                    <Button
                      size="sm" variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={() => setDeleteTarget(o)}
                      title="Delete this order"
                      data-testid={`delete-order-${o.order_number}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewOrderDialog onClose={() => setShowNew(false)} onCreated={async (ord) => { setShowNew(false); await load(); setActiveOrder(ord); }} />}
      {activeOrder && <OrderDetailDialog order={activeOrder} onClose={() => { setActiveOrder(null); load(); }} onRequestDelete={(o) => { setActiveOrder(null); setDeleteTarget(o); }} />}
      {deleteTarget && (
        <DeleteOrderDialog
          order={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={async () => { setDeleteTarget(null); await load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────── New Order Dialog ───────────────────────
// Taxonomy mirrored from backend/routes/orders.py:RETAIL_ORDER_TYPES.
// Keep both lists in lock-step — if you add a type here, add it there.
const RETAIL_ORDER_TYPES = [
  { v: 'paid',          label: 'Paid',          hint: 'Standard NYC DOE P.O. — fully billable' },
  { v: 'complimentary', label: 'Complimentary', hint: '$0 goodwill drop — no PO required, invoice stamps NO CHARGE' },
  { v: 'demo',          label: 'Demo',          hint: 'Short-term prospect eval — same $0 stamp, tracked for conversion' },
  { v: 'internal_qa',   label: 'Internal / QA', hint: 'Ops bench build — excluded from every customer-facing report' },
];
const RETAIL_COMP_REASONS = [
  { v: 'demo',              label: 'Demo'                     },
  { v: 'vip_eval',          label: 'VIP evaluation'           },
  { v: 'replacement',       label: 'Replacement for an issue' },
  { v: 'marketing',         label: 'Marketing / outreach'     },
  { v: 'internal_qa',       label: 'Internal QA / testing'    },
  { v: 'promotional_pitch', label: 'Promotional pitch'        },
  { v: 'other',             label: 'Other (see admin notes)'  },
];

function NewOrderDialog({ onClose, onCreated }) {
  const [source, setSource] = useState('manual');   // 'manual' | 'quote'
  const [quoteId, setQuoteId] = useState('');
  const [orderType, setOrderType] = useState('paid');
  const [compReason, setCompReason] = useState('');
  const [compApprovedBy, setCompApprovedBy] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState(new Date().toISOString().slice(0, 10));
  const [billTo, setBillTo] = useState({ ...EMPTY_CONTACT });
  const [shipTo, setShipTo] = useState({ ...EMPTY_CONTACT });
  const [items, setItems] = useState([{ sku: '', name: '', quantity: 1, unit_price: 149.00 }]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState(null);

  const isComp = orderType !== 'paid';

  // Pre-load quotes list for convert-from-quote flow.
  // Surface loading/empty/error states so the dropdown is never silently empty.
  const loadQuotes = useCallback(async () => {
    setQuotesLoading(true);
    setQuotesError(null);
    try {
      const r = await axios.get(`${API}/api/quotes/list?limit=200`, authCfg());
      setQuotes(r.data.quotes || []);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setQuotesError(msg);
      toast.error('Could not load quotes list: ' + msg);
    } finally {
      setQuotesLoading(false);
    }
  }, []);
  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  const onSelectQuote = (qnum) => {
    // Quotes use quote_number as primary key (quote_id is typically null),
    // so we match by quote_number here.
    setQuoteId(qnum);
    const q = quotes.find(x => x.quote_number === qnum);
    if (q) {
      setBillTo({ ...EMPTY_CONTACT, ...(q.contact || {}) });
      setShipTo({ ...EMPTY_CONTACT, ...(q.contact || {}) });
      setItems(q.items?.map(it => ({
        sku: it.sku, name: it.name, quantity: it.quantity, unit_price: it.unit_price,
      })) || [{ sku: '', name: '', quantity: 1, unit_price: 0 }]);
    }
  };

  const addItem = () => setItems([...items, { sku: '', name: '', quantity: 1, unit_price: 149.00 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => {
    const copy = [...items];
    copy[idx][field] = (field === 'quantity' || field === 'unit_price') ? Number(val) : val;
    setItems(copy);
  };

  const submit = async () => {
    if (!isComp && !poNumber.trim()) return toast.error("PO number is required for paid orders. Switch Order Type to Complimentary/Demo/QA if this is a no-charge drop.");
    if (isComp && !compReason) return toast.error("Pick a Comp Reason before submitting.");
    if (!billTo.school_name || !billTo.school_address) return toast.error("Bill-to school name + address are required");
    if (!shipTo.school_name || !shipTo.school_address) return toast.error("Ship-to school name + address are required");
    if (items.some(it => !it.sku || !it.quantity)) return toast.error("Every line item needs a SKU and quantity");

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/orders`, {
        source, quote_id: source === 'quote' ? quoteId : null,
        order_type: orderType,
        comp_reason: isComp ? compReason : null,
        comp_approved_by: isComp ? (compApprovedBy.trim() || null) : null,
        po_number: poNumber.trim() || null,
        po_date: poDate,
        bill_to: billTo, ship_to: shipTo,
        items, notes,
      }, authCfg());
      toast.success(
        isComp
          ? `Comp order ${r.data.order_number} created (ref ${r.data.po_number || 'auto-stamped'})`
          : `Order ${r.data.order_number} created`
      );
      onCreated(r.data);
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="new-order-dialog">
        <DialogHeader>
          <DialogTitle>New Order — Record Received PO</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* ─── Order Type pill-row (billable vs comp vs demo vs QA) ─── */}
          <div>
            <Label className="text-xs">Order Type</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="retail-ordertype-row">
              {RETAIL_ORDER_TYPES.map(t => {
                const active = orderType === t.v;
                const activeBg = t.v === 'paid' ? 'bg-slate-800 text-white' : 'bg-amber-400 text-slate-900';
                return (
                  <button
                    key={t.v}
                    type="button"
                    data-testid={`retail-ordertype-${t.v}`}
                    onClick={() => { setOrderType(t.v); if (t.v === 'paid') setCompReason(''); }}
                    title={t.hint}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active ? `${activeBg} border-slate-800` : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {RETAIL_ORDER_TYPES.find(t => t.v === orderType)?.hint}
            </div>
          </div>

          {/* ─── Amber comp block — only for non-paid types ─── */}
          {isComp && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="retail-comp-block">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">
                    Comp Reason <span className="text-red-600">*</span>
                  </Label>
                  <Select value={compReason} onValueChange={setCompReason}>
                    <SelectTrigger className="h-9 mt-1 bg-white" data-testid="retail-comp-reason">
                      <SelectValue placeholder="— select a reason —" />
                    </SelectTrigger>
                    <SelectContent>
                      {RETAIL_COMP_REASONS.map(r => (
                        <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Approved By (email, optional)</Label>
                  <Input
                    type="email"
                    className="h-9 mt-1 bg-white"
                    value={compApprovedBy}
                    onChange={(e) => setCompApprovedBy(e.target.value)}
                    placeholder="Defaults to your admin email"
                    data-testid="retail-comp-approved-by"
                  />
                </div>
              </div>
              <div className="text-[11px] text-amber-800 leading-snug">
                <b>No charge.</b> PO # is optional — if blank, the system auto-stamps a sortable
                {' '}<code className="font-mono text-[11px]">COMP-/DEMO-/QAIN-YYMMDD-XXXX</code> reference.
                Invoice PDF stamps <b>“COMPLIMENTARY · NO CHARGE · $0.00”</b>. Reps earn no commission.
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs">Source:</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-48 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual entry</SelectItem>
                <SelectItem value="quote">Convert from existing Quote</SelectItem>
              </SelectContent>
            </Select>
            {source === 'quote' && (
              <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                <Select value={quoteId} onValueChange={onSelectQuote} disabled={quotesLoading || quotes.length === 0}>
                  <SelectTrigger className="flex-1 h-8" data-testid="select-source-quote">
                    <SelectValue placeholder={
                      quotesLoading ? 'Loading quotes…'
                      : quotes.length === 0 ? 'No quotes available'
                      : `Pick a Quote… (${quotes.length} available)`
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {quotes.map(q => (
                      <SelectItem key={q.quote_number} value={q.quote_number}>
                        {q.quote_number} — {q.contact?.school_name} (${(q.subtotal || 0).toLocaleString()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" size="sm" variant="outline" onClick={loadQuotes} disabled={quotesLoading} title="Reload quotes list" data-testid="reload-quotes-btn">
                  {quotesLoading ? '…' : '↻'}
                </Button>
              </div>
            )}
          </div>
          {source === 'quote' && (quotesError || quotes.length === 0) && !quotesLoading && (
            <div className="text-xs rounded border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
              {quotesError
                ? <>Couldn't load quotes: <b>{quotesError}</b>. Make sure you're logged in as a Super Admin, then click the reload button.</>
                : <>No quotes found. Generate one from the public Quote Calculator (<span className="font-mono">/quote</span>) first, or switch back to <b>Manual entry</b>.</>
              }
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>{isComp ? 'PO # (OPTIONAL — auto-stamped if blank)' : 'School PO Number *'}</Label>
              <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)}
                placeholder={isComp ? 'Leave blank to auto-stamp COMP-YYMMDD-XXXX' : 'e.g. NYC-2026-04-12345'}
                data-testid="field-po-number" />
            </div>
            <div><Label>PO Date</Label>
              <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
            </div>
          </div>

          {['Bill To', 'Ship To'].map((title, i) => {
            const state = i === 0 ? billTo : shipTo;
            const setter = i === 0 ? setBillTo : setShipTo;
            return (
              <div key={title} className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm text-indigo-700">{title}</div>
                  {i === 1 && <Button size="sm" variant="outline" onClick={() => setShipTo({ ...billTo })}>Copy from Bill To</Button>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="School name" value={state.school_name} onChange={(e) => setter({ ...state, school_name: e.target.value })} />
                  <Input placeholder="DBN (optional)" value={state.school_dbn} onChange={(e) => setter({ ...state, school_dbn: e.target.value })} />
                  <Input className="col-span-2" placeholder="Street address" value={state.school_address} onChange={(e) => setter({ ...state, school_address: e.target.value })} />
                  <Input placeholder="Contact name" value={state.contact_name} onChange={(e) => setter({ ...state, contact_name: e.target.value })} />
                  <Input placeholder="Contact email" value={state.contact_email} onChange={(e) => setter({ ...state, contact_email: e.target.value })} />
                  <Input className="col-span-2" placeholder="Contact phone" value={state.contact_phone} onChange={(e) => setter({ ...state, contact_phone: e.target.value })} />
                </div>
              </div>
            );
          })}

          <div className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm text-indigo-700">Line Items</div>
              <Button size="sm" variant="outline" onClick={addItem}><Plus size={14} /> Add</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-[1.5fr_3fr_0.7fr_1fr_auto] gap-2 items-center" data-testid={`line-item-${idx}`}>
                  <Input placeholder="SKU" value={it.sku} onChange={(e) => updateItem(idx, 'sku', e.target.value)} />
                  <Input placeholder="Description" value={it.name} onChange={(e) => updateItem(idx, 'name', e.target.value)} />
                  <Input type="number" min="1" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} />
                  <Input type="number" step="0.01" placeholder="Price" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} />
                  <Button size="icon" variant="ghost" onClick={() => removeItem(idx)} disabled={items.length === 1}><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Notes / Special Instructions</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={isComp ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : 'bg-indigo-600 hover:bg-indigo-700'}
            data-testid="submit-new-order"
          >
            {submitting ? 'Creating…' : (isComp ? 'Create + Issue Comp Order ($0)' : 'Create Order')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────── Order Detail Dialog ───────────────────────
function PoDateEditor({ order, onUpdated }) {
  // Inline editor for the order's PO Date. Defaults to the existing
  // value (or today). Mirrors the quote `effective_date` flow:
  // backdate / forward-date during a revision while the original
  // `created_at` audit timestamp is preserved server-side.
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(order.po_date || new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!val) { toast.error('PO date is required'); return; }
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/api/orders/${order.order_id}/po-date`,
        { po_date: val }, authCfg());
      onUpdated && onUpdated(r.data);
      toast.success(`PO date updated to ${val}`);
      setEditing(false);
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs">{order.po_date || '—'}</span>
        <button
          type="button"
          onClick={() => { setVal(order.po_date || new Date().toISOString().slice(0, 10)); setEditing(true); }}
          className="text-[10px] text-indigo-600 hover:underline"
          data-testid={`po-date-edit-${order.order_id}`}
        >
          edit
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input type="date" value={val} onChange={e => setVal(e.target.value)} className="h-7 text-xs w-36" data-testid={`po-date-input-${order.order_id}`} />
      <button type="button" disabled={saving} onClick={save} className="text-[10px] text-emerald-600 hover:underline disabled:opacity-50" data-testid={`po-date-save-${order.order_id}`}>save</button>
      <button type="button" onClick={() => setEditing(false)} className="text-[10px] text-gray-500 hover:underline">cancel</button>
    </div>
  );
}


function ContactsEditor({ order, onUpdated, onClose }) {
  // v6.32.92 — super-admin modal for correcting legacy order contact/display
  // fields via the v6.32.91 PATCH /api/orders/{id}/contacts endpoint.
  // Operator-locked allowlist: 8 fields total (bill_to/ship_to × {contact_email,
  // contact_name, school_name, school_address}). Audit-row-first, side-effect-
  // free. Requires a non-empty correction_reason (≥3 chars). Shows before/after
  // after submission so operator can eyeball the diff.
  const [billTo, setBillTo] = useState({
    contact_email: order.bill_to?.contact_email || '',
    contact_name: order.bill_to?.contact_name || '',
    school_name: order.bill_to?.school_name || '',
    school_address: order.bill_to?.school_address || '',
  });
  const [shipTo, setShipTo] = useState({
    contact_email: order.ship_to?.contact_email || '',
    contact_name: order.ship_to?.contact_name || '',
    school_name: order.ship_to?.school_name || '',
    school_address: order.ship_to?.school_address || '',
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [copyBillToShip, setCopyBillToShip] = useState(false);

  // Diff: which fields were changed from original
  const diff = (side, original) => {
    const d = {};
    Object.entries(side).forEach(([k, v]) => {
      const orig = original?.[k] ?? '';
      if ((v ?? '').trim() !== (orig ?? '').trim() && (v ?? '').trim() !== '') {
        d[k] = v.trim();
      }
    });
    return d;
  };

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error('Correction reason must be at least 3 characters');
      return;
    }
    const billChanges = diff(billTo, order.bill_to);
    let shipSource = shipTo;
    if (copyBillToShip) {
      shipSource = { ...billTo };
    }
    const shipChanges = diff(shipSource, order.ship_to);

    if (Object.keys(billChanges).length === 0 && Object.keys(shipChanges).length === 0) {
      toast.error('No changes to apply');
      return;
    }

    const body = { correction_reason: reason.trim() };
    if (Object.keys(billChanges).length > 0) body.bill_to = billChanges;
    if (Object.keys(shipChanges).length > 0) body.ship_to = shipChanges;

    setSaving(true);
    try {
      const r = await axios.patch(`${API}/api/orders/${order.order_id}/contacts`,
        body, authCfg());
      toast.success(`Contacts updated (audit_id=${r.data.audit_id.slice(0, 8)}…, ${r.data.fields_changed.length} field(s))`);
      onUpdated && onUpdated(r.data.order);
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation array — surface first error clearly
        const first = detail[0];
        toast.error(`${first.loc?.join('.')}: ${first.msg}`);
      } else if (typeof detail === 'object' && detail?.reason) {
        toast.error(`${detail.reason}: ${detail.guidance || ''}`);
      } else {
        toast.error('Update failed: ' + (detail || e.message));
      }
    } finally {
      setSaving(false);
    }
  };

  const renderSide = (label, state, setState, original) => (
    <div className="rounded-lg border border-gray-200 p-3 space-y-2">
      <div className="text-xs font-semibold text-indigo-700 uppercase">{label}</div>
      {['school_name', 'school_address', 'contact_name', 'contact_email'].map((k) => {
        const changed = (state[k] ?? '').trim() !== (original?.[k] ?? '').trim() && (state[k] ?? '').trim() !== '';
        return (
          <div key={k}>
            <Label className="text-[10px] text-gray-500 uppercase">{k.replace('_', ' ')}</Label>
            <Input
              value={state[k] || ''}
              onChange={(e) => setState({ ...state, [k]: e.target.value })}
              className={`h-8 text-xs font-mono ${changed ? 'border-amber-400 bg-amber-50' : ''}`}
              data-testid={`contacts-${label.toLowerCase().replace(' ', '-')}-${k}`}
            />
            {changed && (
              <div className="text-[10px] text-amber-600 mt-0.5">
                was: <span className="font-mono">{original?.[k] || '(empty)'}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contacts-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pen size={14} /> Edit contacts — {order.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-xs text-indigo-900">
          <div className="font-semibold mb-1">Narrow-scope contact/display correction (v6.32.91 audited PATCH)</div>
          <div>Allowed fields: contact_email, contact_name, school_name, school_address on bill_to + ship_to. PO #, status, totals, line items, order number, school DBN are <strong>protected</strong> and cannot be edited here. Every save writes a full before/after snapshot to the audit log.</div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-2">
          {renderSide('Bill To', billTo, setBillTo, order.bill_to || {})}
          {renderSide('Ship To', shipTo, setShipTo, order.ship_to || {})}
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-700 mt-1">
          <input
            type="checkbox"
            checked={copyBillToShip}
            onChange={(e) => {
              setCopyBillToShip(e.target.checked);
              if (e.target.checked) setShipTo({ ...billTo });
            }}
            data-testid="contacts-copy-bill-to-ship"
          />
          Copy Bill To → Ship To on save (use when both addresses should always match)
        </label>

        <div>
          <Label className="text-[10px] text-gray-500 uppercase">Correction reason (required, ≥3 chars)</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Fix typo on contact_email — teacher reported misspelling"
            className="text-xs"
            rows={2}
            data-testid="contacts-reason"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="contacts-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || reason.trim().length < 3}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="contacts-submit"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OrderDetailDialog({ order: initial, onClose, onRequestDelete }) {
  const [order, setOrder] = useState(initial);
  const [showSig, setShowSig] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const sigRef = useRef(null);
  const fileInputRef = useRef(null);
  const [signedBy, setSignedBy] = useState(order.signed_by || '');
  const [deliveryDate, setDeliveryDate] = useState(order.delivery_date || new Date().toISOString().slice(0, 10));
  const [carrier, setCarrier] = useState(order.carrier || '');
  const [tracking, setTracking] = useState(order.tracking || '');

  // v6.38.27 — Commission tracking state
  const [reps, setReps] = useState([]);
  const [comm, setComm] = useState({
    sales_rep:                order.sales_rep || '',
    commission_pct:           order.commission_pct ?? 15,
    commission_amount:        order.commission_amount ?? 0,
    commission_amount_manual: order.commission_amount_manual || false,
    commission_paid:          order.commission_paid || false,
    commission_paid_date:     order.commission_paid_date || '',
  });
  const [savingComm, setSavingComm] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/commissions/reps`, authCfg())
      .then(res => setReps(res.data.reps || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Auto-recompute commission amount when pct or subtotal changes,
    // unless the user has flagged the amount as manually overridden.
    if (comm.commission_amount_manual) return;
    const amt = Math.round((order.subtotal || 0) * (comm.commission_pct / 100) * 100) / 100;
    if (amt !== comm.commission_amount) {
      setComm(prev => ({ ...prev, commission_amount: amt }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comm.commission_pct, order.subtotal, comm.commission_amount_manual]);

  const saveCommission = async () => {
    setSavingComm(true);
    try {
      const r = await axios.patch(
        `${API}/api/orders/${order.order_id}/commission`, comm, authCfg()
      );
      setOrder(r.data);
      toast.success(`Commission updated${comm.sales_rep ? ' — ' + comm.sales_rep : ''}`);
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSavingComm(false);
    }
  };

  const resetCommissionToAuto = () => {
    const amt = Math.round((order.subtotal || 0) * (comm.commission_pct / 100) * 100) / 100;
    setComm(prev => ({ ...prev, commission_amount_manual: false, commission_amount: amt }));
  };

  const patchStatus = async (status, extra = {}) => {
    try {
      const r = await axios.patch(`${API}/api/orders/${order.order_id}/status`,
        { status, ...extra }, authCfg());
      setOrder(r.data);
      toast.success(`Marked as ${STATUS_META[status]?.label}`);
    } catch (e) {
      toast.error('Update failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const captureSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error('Please sign first');
    const b64 = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
    await patchStatus('delivered', {
      signed_by: signedBy, delivery_date: deliveryDate,
      signature_image_b64: b64, carrier, tracking,
    });
    setShowSig(false);
  };

  const uploadSignedPOD = async (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result;
      await patchStatus('delivered', {
        signed_by: signedBy || 'Scanned POD on file',
        delivery_date: deliveryDate,
        pod_upload_b64: b64, pod_upload_name: file.name,
        carrier, tracking,
      });
    };
    reader.readAsDataURL(file);
  };

  // Download a protected PDF as a blob (Bearer token can't ride along on a plain
  // <a target="_blank"> navigation, which is why the live-site "blank screen" bug
  // happened). Blob-download also gives us a real filename and a surfaced error
  // when auth fails, instead of a bare 401 JSON page.
  const downloadDoc = async (path, filename) => {
    const loading = toast.loading(`Preparing ${filename}…`);
    try {
      const r = await axios.get(`${API}${path}`, {
        ...authCfg(),
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([r.data], { type: 'application/pdf' })
      );
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Keep the object URL alive for a second so the download actually fires in
      // Safari / Firefox before we revoke it.
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1500);
      toast.success(`Downloaded ${filename}`, { id: loading });
    } catch (e) {
      // When responseType='blob', error bodies are Blobs — try to read them.
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail || text;
        } catch { detail = e.message; }
      }
      toast.error(`Download failed: ${detail}`, { id: loading });
    }
  };

  // Print a protected PDF (same Bearer-blob approach as downloadDoc). Uses a
  // hidden iframe so it works regardless of popup-blocker settings. The iframe's
  // contentWindow.print() call is compatible with Chrome/Edge/Firefox/Safari PDF
  // viewers.
  const printDoc = async (path, filename) => {
    const loading = toast.loading(`Preparing ${filename} for print…`);
    try {
      const r = await axios.get(`${API}${path}`, {
        ...authCfg(),
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([r.data], { type: 'application/pdf' })
      );
      // Reuse any existing print iframe so we don't leak DOM on repeated prints.
      const existing = document.getElementById('mosaic-print-iframe');
      if (existing) existing.remove();
      const iframe = document.createElement('iframe');
      iframe.id = 'mosaic-print-iframe';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          toast.success(`Print dialog opened for ${filename}`, { id: loading });
        } catch (err) {
          toast.error('Could not open print dialog: ' + err.message, { id: loading });
        }
      };
      // Cleanup after a generous delay (user may take time in the print dialog).
      setTimeout(() => {
        const el = document.getElementById('mosaic-print-iframe');
        if (el) el.remove();
        window.URL.revokeObjectURL(blobUrl);
      }, 120000);
    } catch (e) {
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail || text;
        } catch { detail = e.message; }
      }
      toast.error(`Print failed: ${detail}`, { id: loading });
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-indigo-700">{order.order_number}</span>
            <Badge variant="outline" className={STATUS_META[order.status]?.color}>{STATUS_META[order.status]?.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-start justify-between">
              <div className="text-xs font-semibold text-indigo-700 uppercase">Bill To</div>
              <button
                type="button"
                onClick={() => setShowContacts(true)}
                className="text-[10px] text-indigo-600 hover:underline"
                data-testid={`contacts-edit-${order.order_id}`}
              >
                edit
              </button>
            </div>
            <div className="mt-1">{order.bill_to?.school_name} {order.bill_to?.school_dbn ? `(${order.bill_to.school_dbn})` : ''}</div>
            <div className="text-xs text-gray-500">{order.bill_to?.school_address}</div>
            <div className="text-xs text-gray-700 mt-1">{order.bill_to?.contact_name}</div>
            <div className="text-xs text-gray-500 font-mono">{order.bill_to?.contact_email}</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs font-semibold text-indigo-700 uppercase">Ship To</div>
            <div className="mt-1">{order.ship_to?.school_name}</div>
            <div className="text-xs text-gray-500">{order.ship_to?.school_address}</div>
            <div className="text-xs text-gray-700 mt-1">{order.ship_to?.contact_name}</div>
            <div className="text-xs text-gray-500 font-mono">{order.ship_to?.contact_email}</div>
          </div>
          <div><span className="text-xs text-gray-500">PO #</span><div className="font-mono text-xs">{order.po_number}</div></div>
          <div>
            <span className="text-xs text-gray-500">PO Date</span>
            <PoDateEditor order={order} onUpdated={setOrder} />
          </div>
          <div><span className="text-xs text-gray-500">Total</span><div className="font-semibold">${(order.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
        </div>

        {/* Source Quote linkage — lets you spot any mismatch between the
            original quote's school/subtotal and the order's bill-to/total. */}
        {order.quote_ref && (
          <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/50 p-3 mt-3" data-testid="source-quote-card">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-indigo-700 uppercase flex items-center gap-1.5">
                <FileText size={12} /> Order ← from Quote
              </div>
              <Badge variant="outline" className="bg-white border-indigo-300 text-indigo-700 text-[10px]">
                CONVERTED · {order.quote_ref.converted_at?.slice(0, 10) || '—'}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-gray-500">Quote #</div>
                <div className="font-mono font-semibold text-indigo-700">{order.quote_ref.quote_number}</div>
              </div>
              <div>
                <div className="text-gray-500">Quote created</div>
                <div className="font-semibold">{order.quote_ref.created_at?.slice(0, 10) || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">Quote subtotal</div>
                <div className="font-semibold">
                  ${(order.quote_ref.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  {Math.abs((order.quote_ref.subtotal || 0) - (order.total || 0)) > 0.01 && (
                    <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-full" title="Quote subtotal and order total differ — verify line items">
                      Δ ${Math.abs((order.quote_ref.subtotal || 0) - (order.total || 0)).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 pt-3 mt-3">
          <div className="text-xs font-semibold text-indigo-700 uppercase mb-2">Documents — Click main button to download, printer icon to print</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
            {/* Invoice */}
            <div className="flex">
              <Button
                variant="outline" className="flex-1 gap-2 rounded-r-none border-r-0"
                onClick={() => downloadDoc(`/api/orders/${order.order_id}/invoice.pdf`, `${order.invoice_number}_Invoice.pdf`)}
                data-testid="dl-invoice"
              >
                <FileText size={14} /> Invoice
              </Button>
              <Button
                variant="outline" className="rounded-l-none px-2.5"
                title="Print Invoice"
                onClick={() => printDoc(`/api/orders/${order.order_id}/invoice.pdf`, `${order.invoice_number}_Invoice.pdf`)}
                data-testid="print-invoice"
              >
                <Printer size={14} />
              </Button>
            </div>
            {/* Packing Slip */}
            <div className="flex">
              <Button
                variant="outline" className="flex-1 gap-2 rounded-r-none border-r-0"
                onClick={() => downloadDoc(`/api/orders/${order.order_id}/packing.pdf`, `${order.packing_number}_PackingSlip.pdf`)}
                data-testid="dl-packing"
              >
                <Package size={14} /> Packing Slip
              </Button>
              <Button
                variant="outline" className="rounded-l-none px-2.5"
                title="Print Packing Slip"
                onClick={() => printDoc(`/api/orders/${order.order_id}/packing.pdf`, `${order.packing_number}_PackingSlip.pdf`)}
                data-testid="print-packing"
              >
                <Printer size={14} />
              </Button>
            </div>
            {/* POD Slip */}
            <div className="flex">
              <Button
                variant="outline" className="flex-1 gap-2 rounded-r-none border-r-0"
                onClick={() => downloadDoc(`/api/orders/${order.order_id}/pod.pdf`, `${order.pod_number}_POD.pdf`)}
                data-testid="dl-pod"
              >
                <Truck size={14} /> POD Slip
              </Button>
              <Button
                variant="outline" className="rounded-l-none px-2.5"
                title="Print POD Slip"
                onClick={() => printDoc(`/api/orders/${order.order_id}/pod.pdf`, `${order.pod_number}_POD.pdf`)}
                data-testid="print-pod"
              >
                <Printer size={14} />
              </Button>
            </div>
            {/* Signed POD (conditional) */}
            {order.pod_upload && (
              <div className="flex">
                <Button
                  variant="outline"
                  className="flex-1 gap-2 rounded-r-none border-r-0 bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                  onClick={() => downloadDoc(`/api/orders/${order.order_id}/pod-upload.pdf`, `${order.pod_number}_POD_SIGNED.pdf`)}
                  data-testid="dl-pod-upload"
                >
                  <Download size={14} /> Signed POD
                </Button>
                <Button
                  variant="outline"
                  className="rounded-l-none px-2.5 bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                  title="Print Signed POD"
                  onClick={() => printDoc(`/api/orders/${order.order_id}/pod-upload.pdf`, `${order.pod_number}_POD_SIGNED.pdf`)}
                  data-testid="print-pod-upload"
                >
                  <Printer size={14} />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
          <div className="text-xs font-semibold text-indigo-700 uppercase">Fulfillment Actions</div>

          {/* ─── Fulfillment Router: Digital-activate / USB queue / Split ─── */}
          {(() => {
            // Prefix taxonomy — mirror backend `_RETAIL_DIGITAL_PREFIXES` in
            // backend/routes/orders.py. `MOS-TK-` is a legacy alias kept for
            // round-trip safety; current catalog ships `MOS-TKT-*` toolkits.
            const DIG_PREFIXES = ['MOS-DIG-', 'MOS-TKT-', 'MOS-TK-'];
            const USB_PREFIXES = ['MOS-SSE-', 'MOS-USB-'];
            const isDigital = (sku) =>
              DIG_PREFIXES.some(p => (sku || '').toUpperCase().startsWith(p));
            const isUsb = (sku) =>
              USB_PREFIXES.some(p => (sku || '').toUpperCase().startsWith(p));

            const items = order.items || [];
            const digitalItems = items.filter(i => isDigital(i.sku));
            const usbItems     = items.filter(i => isUsb(i.sku));
            const otherItems   = items.filter(i => !isDigital(i.sku) && !isUsb(i.sku));

            const alreadyConverted = !!order.converted_to_lerno_order_id;
            const hasDigital = digitalItems.length > 0;
            const hasUsb     = usbItems.length > 0;
            const hasOther   = otherItems.length > 0;
            const allDigital = hasDigital && !hasUsb && !hasOther;
            const allUsb     = hasUsb && !hasDigital && !hasOther;

            // Nothing to say for pure print-only orders (books, posters,
            // physical bundles) — they use the standard Pack/Ship pipeline.
            if (!alreadyConverted && !hasDigital && !hasUsb) return null;

            // ── Already-converted banner (idempotent re-open of a comp'd row) ──
            if (alreadyConverted) {
              return (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3"
                     data-testid="already-converted-block">
                  <div className="text-sm font-semibold text-emerald-900 mb-0.5">
                    Digital / Cloud-based order · activated
                  </div>
                  <div className="text-xs text-emerald-800 leading-snug">
                    Already converted to <code className="font-mono text-[11px] bg-white px-1 py-0.5 rounded">{order.converted_to_lerno_order_id}</code>.
                    Download link + ZIP live on <a className="underline" href="/admin/lerno-orders" target="_blank" rel="noreferrer">/admin/lerno-orders</a>.
                  </div>
                </div>
              );
            }

            // Shared Convert-to-Digital button, used by the all-digital path.
            const convertBtn = (
              <Button
                size="sm"
                onClick={async () => {
                  const coord = (order.bill_to || {}).contact_email || '';
                  if (!coord.includes('@')) {
                    toast.error("Bill-to contact email is required — the Lerno flow emails the ELL coordinator a download link.");
                    return;
                  }
                  if (!window.confirm(
                    `Convert ${order.order_number} to a Digital/Lerno order?\n\n` +
                    `• Lerno order spawned with the same comp reason + approver\n` +
                    `• ZIP built, download link emailed to ${coord}\n` +
                    `• This retail order marked CANCELLED (not deleted)\n\nProceed?`
                  )) return;
                  try {
                    const r = await axios.post(
                      `${API}/api/orders/${order.order_id}/convert-to-digital`,
                      { issue_immediately: true },
                      authCfg(),
                    );
                    if (r.data.status === 'already_converted') {
                      toast.info(`Already converted to ${r.data.lerno_order_id}`);
                    } else {
                      toast.success(
                        `Converted to ${r.data.lerno_order_id}` +
                        (r.data.email_sent ? ` · email sent to ${coord}` : ' · ZIP ready (check email logs)')
                      );
                    }
                    try {
                      const fresh = await axios.get(
                        `${API}/api/orders/${order.order_id}`, authCfg());
                      setOrder(fresh.data);
                    } catch (_) { /* dialog will refresh on next open */ }
                  } catch (e) {
                    toast.error('Convert failed: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 shrink-0"
                data-testid="convert-to-digital-btn"
              >
                Convert to Digital Order
              </Button>
            );

            // ── Case A: 100% digital — one-tap activation. ──
            if (allDigital) {
              return (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2"
                     data-testid="convert-to-digital-block">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-xs leading-snug">
                      <div className="font-semibold text-amber-900 text-sm mb-0.5">
                        Digital / Cloud-based order
                      </div>
                      <div className="text-amber-800">
                        All {digitalItems.length} line{digitalItems.length === 1 ? '' : 's'} are digital SKUs ({digitalItems.map(i => i.sku).join(', ')}).
                        {' '}The retail Pack/Ship pipeline doesn't apply.
                        {' '}Click <b>Convert to Digital Order</b> to spawn a matching
                        Lerno order, build the licensed ZIP, email the coordinator,
                        and cancel this retail row — one tap.
                      </div>
                    </div>
                    {convertBtn}
                  </div>
                </div>
              );
            }

            // ── Case B: 100% physical USB (MOS-SSE-* / MOS-USB-*) — route to USB queue. ──
            if (allUsb) {
              return (
                <div className="rounded-lg border border-indigo-300 bg-indigo-50 p-3 space-y-2"
                     data-testid="route-to-usb-block">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-xs leading-snug">
                      <div className="font-semibold text-indigo-900 text-sm mb-0.5">
                        Sealed USB order
                      </div>
                      <div className="text-indigo-800">
                        All {usbItems.length} line{usbItems.length === 1 ? '' : 's'} are physical sealed-USB SKUs ({usbItems.map(i => i.sku).join(', ')}).
                        {' '}There is nothing to activate digitally — fulfill this via the
                        {' '}<b>USB Orders queue</b> (mint drives, seal, ship, deliver).
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => window.open('/admin/usb-orders', '_blank', 'noreferrer')}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                      data-testid="open-usb-queue-btn"
                    >
                      Open USB Queue
                    </Button>
                  </div>
                </div>
              );
            }

            // ── Case C: Mixed digital + USB (+ possibly print) — needs split. ──
            //    Visible panel so the operator always sees why the single-tap
            //    Convert button is unavailable.
            return (
              <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 space-y-2"
                   data-testid="mixed-order-split-block">
                <div className="text-sm font-semibold text-rose-900 mb-0.5">
                  Mixed order — split required before activation
                </div>
                <div className="text-xs text-rose-800 leading-snug">
                  This order mixes line types, so there is no single fulfillment
                  path. Split it into one order per channel, then activate each:
                </div>
                <ul className="text-xs text-rose-800 list-disc ml-5 space-y-0.5">
                  {hasDigital && (
                    <li data-testid="split-hint-digital">
                      <b>{digitalItems.length}</b> digital line{digitalItems.length === 1 ? '' : 's'} ({digitalItems.map(i => i.sku).join(', ')}) → new Digital order → Convert to Lerno
                    </li>
                  )}
                  {hasUsb && (
                    <li data-testid="split-hint-usb">
                      <b>{usbItems.length}</b> sealed-USB line{usbItems.length === 1 ? '' : 's'} ({usbItems.map(i => i.sku).join(', ')}) → USB Orders queue
                    </li>
                  )}
                  {hasOther && (
                    <li data-testid="split-hint-print">
                      <b>{otherItems.length}</b> print/other line{otherItems.length === 1 ? '' : 's'} ({otherItems.map(i => i.sku).join(', ')}) → stays here, Pack/Ship as usual
                    </li>
                  )}
                </ul>
                <div className="text-[11px] text-rose-700 italic">
                  Tip: duplicate this order via <b>New Order</b>, remove the
                  non-matching lines, save, then come back here and cancel this
                  parent once the children are filed.
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Button size="sm" variant="outline" onClick={() => patchStatus('invoiced')} disabled={['invoiced','packed','shipped','delivered'].includes(order.status)}>Mark Invoiced</Button>
            <Button size="sm" variant="outline" onClick={() => patchStatus('packed')} disabled={['packed','shipped','delivered'].includes(order.status)}>Mark Packed</Button>
            <div className="col-span-2 md:col-span-3 flex gap-2">
              <Input placeholder="Carrier (UPS, FedEx…)" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
              <Input placeholder="Tracking #" value={tracking} onChange={(e) => setTracking(e.target.value)} />
              <Button size="sm" variant="outline" onClick={() => patchStatus('shipped', { carrier, tracking })} disabled={order.status === 'delivered'}>Mark Shipped</Button>
            </div>
          </div>

          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 mt-3">
            <div className="font-semibold text-indigo-700 text-sm mb-2">Mark Delivered (POD capture)</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Input placeholder="Received by (printed name)" value={signedBy} onChange={(e) => setSignedBy(e.target.value)} />
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => setShowSig(true)} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="open-sig-pad"><Pen size={14} /> Sign on screen</Button>
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2" data-testid="upload-signed-pod"><Upload size={14} /> Upload signed PDF</Button>
              <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => {
                if (e.target.files?.[0]) uploadSignedPOD(e.target.files[0]);
              }} />
            </div>
            {order.signed_by && <div className="text-xs text-emerald-700 mt-2">Delivered · signed by <b>{order.signed_by}</b> on {order.delivery_date || '—'}</div>}
          </div>
        </div>

        {/* Commission Tracking (v6.38.27) */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 mt-4" data-testid="order-commission-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-amber-800 uppercase">Sales Rep & Commission</div>
            {order.sales_rep_auto_assigned && comm.sales_rep && (
              <span className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-[1px]" title="Automatically assigned by borough — you can change this">
                AUTO-ASSIGNED BY BOROUGH
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-gray-600">Sales Rep</Label>
              <select
                value={comm.sales_rep}
                onChange={(e) => setComm(prev => ({ ...prev, sales_rep: e.target.value }))}
                className="w-full h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                data-testid="order-rep-select"
              >
                <option value="">— Select rep —</option>
                {reps.map(r => (
                  <option key={r.rep_id || r.name} value={r.name}>{r.name}{r.territory ? ` — ${r.territory}` : ''}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-gray-600">Commission %</Label>
                <Input
                  type="number" step="0.5" min="0" max="100"
                  value={comm.commission_pct}
                  onChange={(e) => setComm(prev => ({ ...prev, commission_pct: parseFloat(e.target.value) || 0 }))}
                  className="h-8 text-xs"
                  data-testid="order-commission-pct"
                />
              </div>
              <div>
                <Label className="text-[11px] text-gray-600 flex items-center justify-between">
                  <span>Amount ($)</span>
                  {comm.commission_amount_manual && (
                    <button
                      type="button"
                      onClick={resetCommissionToAuto}
                      className="text-[9px] text-blue-600 hover:underline font-medium"
                      data-testid="order-commission-reset-auto"
                      title={`Recompute from ${comm.commission_pct}% × subtotal`}
                    >
                      Reset to auto
                    </button>
                  )}
                </Label>
                <div className="relative">
                  <Input
                    type="number" step="0.01" min="0"
                    value={comm.commission_amount}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      setComm(prev => ({
                        ...prev,
                        commission_amount: isNaN(v) ? 0 : v,
                        commission_amount_manual: true,
                      }));
                    }}
                    className={`h-8 text-xs font-bold pr-14 ${comm.commission_amount_manual ? 'text-amber-800 bg-amber-50 border-amber-400' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`}
                    data-testid="order-commission-amount"
                  />
                  {comm.commission_amount_manual && (
                    <span
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-[8px] font-bold text-amber-800 bg-amber-100 border border-amber-400 rounded px-1 py-[1px]"
                      title="Manually overridden — will not auto-recompute"
                    >
                      MANUAL
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={comm.commission_paid}
                  onChange={(e) => setComm(prev => ({ ...prev, commission_paid: e.target.checked }))}
                  className="rounded"
                  data-testid="order-commission-paid"
                />
                <span className={`font-bold ${comm.commission_paid ? 'text-emerald-700' : 'text-gray-500'}`}>Paid</span>
              </label>
              {comm.commission_paid && (
                <Input
                  type="date"
                  value={comm.commission_paid_date}
                  onChange={(e) => setComm(prev => ({ ...prev, commission_paid_date: e.target.value }))}
                  className="h-7 text-xs w-40"
                  data-testid="order-commission-paid-date"
                />
              )}
            </div>
            <Button
              size="sm"
              onClick={saveCommission}
              disabled={savingComm}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="order-commission-save"
            >
              {savingComm ? 'Saving…' : 'Save Commission'}
            </Button>
          </div>
        </div>

        {showContacts && (
          <ContactsEditor
            order={order}
            onUpdated={setOrder}
            onClose={() => setShowContacts(false)}
          />
        )}

        {showSig && (
          <Dialog open onOpenChange={() => setShowSig(false)}>
            <DialogContent className="max-w-lg" data-testid="sig-pad-dialog">
              <DialogHeader><DialogTitle>Capture Delivery Signature</DialogTitle></DialogHeader>
              <div className="rounded-lg border border-gray-300 bg-white">
                <SignatureCanvas ref={sigRef} penColor="#002FA7"
                  canvasProps={{ width: 480, height: 180, className: 'w-full' }} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => sigRef.current?.clear()}><X size={14} /> Clear</Button>
                <Button onClick={captureSignature} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="save-signature"><CheckCircle2 size={14} /> Save & Mark Delivered</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 gap-1.5 mr-auto"
            onClick={() => onRequestDelete && onRequestDelete(order)}
            data-testid="delete-order-from-detail"
          >
            <Trash2 size={14} /> Delete Order
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────── Delete Order Dialog (password-gated) ───────────────────────
function DeleteOrderDialog({ order, onClose, onDeleted }) {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const canDelete = password.length > 0 && confirmText.trim().toUpperCase() === 'DELETE';

  const submit = async () => {
    if (!canDelete) return;
    setSubmitting(true);
    try {
      const r = await axios.delete(`${API}/api/orders/${order.order_id}`, {
        data: { password, reason },
        ...authCfg(),
      });
      toast.success(`Deleted ${r.data.order_number}${r.data.quote_relinked ? ' · source quote reverted to Pending' : ''}`);
      onDeleted();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error('Delete failed: ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="delete-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle size={18} /> Delete Order
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
            <div className="font-semibold">This cannot be undone.</div>
            <div className="mt-1 text-xs leading-relaxed">
              Order <b className="font-mono">{order.order_number}</b> (PO <b className="font-mono">{order.po_number}</b>)
              for <b>{order.bill_to?.school_name}</b> will be permanently removed. Generated Invoice,
              Packing Slip, and POD PDFs will be deleted from disk.
              {order.quote_ref && (
                <> The linked source Quote <b className="font-mono">{order.quote_ref.quote_number}</b> will revert from
                  &nbsp;<b>Won</b> back to <b>Pending</b> so analytics stay accurate.</>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs">Your Super Admin password *</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Re-enter password to confirm"
              autoFocus
              data-testid="delete-order-password"
            />
          </div>

          <div>
            <Label className="text-xs">Reason (optional, logged for audit)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate / wrong PO / test entry"
              data-testid="delete-order-reason"
            />
          </div>

          <div>
            <Label className="text-xs">
              Type <span className="font-mono font-bold text-red-700">DELETE</span> to confirm *
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              data-testid="delete-order-confirm-text"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canDelete || submitting}
            className="bg-red-600 hover:bg-red-700 gap-1.5"
            data-testid="delete-order-submit"
          >
            <Trash2 size={14} /> {submitting ? 'Deleting…' : 'Delete Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
